import dayjs, { Dayjs } from "dayjs";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";

import { DataGrid } from "@mui/x-data-grid";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Button, FormControl, MenuItem, Select, Stack } from "@mui/material";

import Header from "../Header";
import CommissionPayRollStatus from "./CommissionPayRollStatus";

import { api } from "../../utils";
import { BillingRequestModel } from "../Billing/Billing/Billing";
import { IError, ReportLoadingType } from "../../models/ReportPageModels";
import { CustomNoRowsOverlay, ExportToolbar, PaginationModel } from "../../models/ReportGridModels";


const commissionColumns = [
    { field: 'id', headerName: 'uniqueId', width: 1, sortable: false, hide: true, disableExport: true, },
    {
        field: 'salesmanStr', headerName: 'Salesman', width: 250, sortable: true, editable: false, hide: false,
        renderCell: (param: any) => param.row.highlight === true ? (<p><b>{param.row.salesmanStr}</b></p>) : (<p>{param.row.salesmanStr}</p>)
    },
    { field: 'companyStr', headerName: 'Company', width: 150, sortable: true, editable: false },
    { field: 'product', headerName: 'Product', width: 150, sortable: true, editable: false },
    { field: 'priceStr', headerName: 'Price', width: 125, sortable: true, editable: false },
    { field: 'percentStr', headerName: 'Percent', width: 100, sortable: true, editable: false },
    {
        field: 'commissionAmountStr', headerName: 'Commission Amount', width: 200, sortable: true, editable: false,
        renderCell: (param: any) => param.row.highlight === true ? (<p><b>{param.row.commissionAmountStr}</b></p>) : (<p>{param.row.commissionAmountStr}</p>)
    },
]
const columnVisibility = {
    id: false
}

export interface SalesmanModel {
    id: number;
    sales: string;
}

interface CommissionModel {
    id: string;
    salesman: string;
    salesmanStr: string;
    company: string;
    companyStr: string;
    product: string;
    priceStr: string;
    percentStr: string;
    commissionAmountStr: string;

    highlight: boolean;
    companyTotal: boolean;

    reportBillingUser: string;
}

interface GetSalesmenRequest {
    data: GetSalesmenData;
    error: IError;
}
interface GetSalesmenData {
    salesmen: SalesmanModel[];
}

export interface CheckPayRollModel {
    salesID: number;
    salesman: string;
    hasPayRoll: boolean;
}

interface CommissionResponse {
    data: CommissionData;
    error: IError;
}
interface CommissionData {
    commission: CommissionModel[];
    payRollStatus: CheckPayRollModel[];
}

interface CreatePayRollsRequestModel {
    month: Dayjs;
    commission: CommissionModel[];
    checkPayRoll: CheckPayRollModel[];
}

interface CreatePayrollResponseModel {
    data: CreatePayrollData;
    error: IError;
}
interface CreatePayrollData {
    payRollStatus: CheckPayRollModel[];
}

function Commission() {
    const { enqueueSnackbar } = useSnackbar();

    const [paginationModel, setPaginationModel] = useState<PaginationModel>({
        pageSize: 200,
        page: 0,
    });

    const [month, setMonth] = useState<Dayjs>(dayjs());
    var monthCopy = useRef<Dayjs>(dayjs());

    const [salesmen, setSalesmen] = useState<SalesmanModel[]>([]);
    const [selectedSalesman, setSelectedSalesman] = useState<string>("");

    const [commissionLoadingType, setCommissionLoadingType] = useState<ReportLoadingType>(ReportLoadingType.SelectSalesman); // loading, error, noreports, selectClient

    const [commissions, setCommissions] = useState<CommissionModel[]>([]);

    const [checkPayRoll, setCheckPayRoll] = useState<CheckPayRollModel[]>([]);

    useEffect(() => {
        GetSalesmen();
    }, [])

    const GetSalesmen = async () => {
        try {
            const response: GetSalesmenRequest = await api.get("api/Commission/GetSalesmen", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setSalesmen(response.data.salesmen)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const LoadCommission = async () => {
        setCommissions([]);
        setCheckPayRoll([]);
        setCommissionLoadingType(ReportLoadingType.Loading)

        var reqBody: BillingRequestModel = {
            month: monthCopy.current,
            client: selectedSalesman
        };

        try {
            const response: CommissionResponse = await api.post("api/Commission/Commission", {
                json: reqBody,
                timeout: 120 * 1000,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setCommissions(response.data.commission);
                setCheckPayRoll(response.data.payRollStatus);

                if (response.data.commission.length === 0)
                    setCommissionLoadingType(ReportLoadingType.Noreports);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
                setCommissionLoadingType(ReportLoadingType.Error);
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
            setCommissionLoadingType(ReportLoadingType.Error);
        }
    }

    const LoadPayroll = async () => {
        setCheckPayRoll([]);
        enqueueSnackbar("Generating PayRolls", { variant: "info" });

        var reqBody: CreatePayRollsRequestModel = {
            month: monthCopy.current,
            commission: commissions,
            checkPayRoll: checkPayRoll
        };

        try {
            const response: CreatePayrollResponseModel = await api.post("api/Commission/CreatePayroll", {
                json: reqBody,
                timeout: 120 * 1000,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setCheckPayRoll(response.data.payRollStatus);
                enqueueSnackbar("Payroll generated successfully", { variant: "success" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function GetDatagridHeight() {
        return window.innerHeight - 100;
    }

    return (
        <>
            <Header headerType={"BILLING"} displayLogout={false} />
            <Stack direction='row'>
                <Stack
                    direction="column"
                    justifyContent="space-between"
                    sx={{ width: "320px" }}>

                    <Stack
                        direction="column"
                        alignItems="center"
                        justifyContent="flex-start"
                        spacing={1.5}>
                        <span>Select month</span>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                format="MM/YYYY"
                                views={['year', 'month']}
                                openTo={'month'}
                                value={month}
                                onChange={(param: any) => {
                                    setMonth(param);
                                    monthCopy.current = dayjs(param);
                                }}
                            />
                        </LocalizationProvider>

                        <p>Salesmen</p>
                        <FormControl sx={{ width: "240px" }}>
                            <Select
                                value={selectedSalesman}
                                onChange={(newValue) => { setSelectedSalesman(newValue.target.value); }}
                                size="small"
                                defaultValue={"All"}>
                                <MenuItem value={"All"} key={0}>All</MenuItem>
                                {salesmen.map((item, index) => (
                                    <MenuItem value={item.sales} key={index + 1}>{item.sales}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button
                            variant="outlined"
                            onClick={LoadCommission}
                            sx={{ left: '32px' }}>
                            Create commission
                        </Button>
                    </Stack>

                    <CommissionPayRollStatus
                        checkPayRoll={checkPayRoll}
                        LoadPayroll={LoadPayroll} />
                </Stack>

                <div style={{ width: '100%', height: GetDatagridHeight() }}>
                    <DataGrid
                        rows={commissions}
                        columns={commissionColumns}
                        columnVisibilityModel={columnVisibility}
                        pagination
                        pageSizeOptions={[10, 15, 20, 50, 100]}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        keepNonExistentRowsSelected={true}
                        disableColumnMenu={false}
                        slots={{
                            toolbar: ExportToolbar,
                            noRowsOverlay: CustomNoRowsOverlay
                        }}
                        slotProps={{
                            noRowsOverlay: {
                                reportLoadingType: commissionLoadingType
                            }
                        }} />
                </div>
            </Stack>
        </>
    )
}

export default Commission;